import { useState } from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { authFetch } from '@/utils/auth-fetch';
import { toast } from 'sonner';

interface RefundDowngradeButtonProps {
  userId: string;
  userEmail: string;
  onComplete?: () => void;
}

export function RefundDowngradeButton({ userId, userEmail, onComplete }: RefundDowngradeButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [reason, setReason] = useState('requested_by_customer');

  const handleRefundAndDowngrade = async () => {
    setIsLoading(true);
    try {
      const response = await authFetch(`/admin/users/${userId}/refund-and-downgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });

      if (!response.ok) throw new Error('Failed to process refund');

      const result = await response.json();
      toast.success(`Refunded ${result.refund.amount / 100} ${result.refund.currency?.toUpperCase() || 'USD'} and downgraded to free tier`);
      
      if (onComplete) onComplete();
    } catch (error) {
      toast.error('Failed to process refund and downgrade');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="destructive" size="sm">
          Refund & Downgrade
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Refund and Downgrade User</AlertDialogTitle>
          <AlertDialogDescription className="space-y-4">
            <p>This will:</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Refund the latest payment for {userEmail}</li>
              <li>Cancel their subscription immediately</li>
              <li>Downgrade them to the free tier</li>
              <li>This action cannot be undone</li>
            </ul>
            
            <div className="mt-4">
              <label className="text-sm font-medium">Refund Reason</label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="requested_by_customer">Customer Request</SelectItem>
                  <SelectItem value="duplicate">Duplicate Payment</SelectItem>
                  <SelectItem value="fraudulent">Fraudulent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleRefundAndDowngrade}
            disabled={isLoading}
            className="bg-red-600 hover:bg-red-700"
          >
            {isLoading ? 'Processing...' : 'Confirm Refund & Downgrade'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}